import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  ArrowDown, ArrowRight, ArrowUpRight, 
  Menu, X, Zap, Heart, Users, Trophy, School, Clock, 
  MapPin, Phone, Mail, Globe, GraduationCap, ChevronLeft, 
  ChevronRight, ChevronDown, Download, Send, Plus, Layout, Sparkles, 
  FileText, Instagram, Facebook, Linkedin, Youtube, Quote, BookOpen, Star, Camera
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from "@/components/ui/button";
import { VisualImageEditable } from "@/components/VisualImageEditable";
import { VisualTextEditable } from "@/components/VisualTextEditable";

const BoldTypography: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Icon resolver helper
  const getIcon = (name: string | any) => {
      if (!name) return Star;
      if (typeof name !== 'string') return name;
      // @ts-ignore
      const Icon = LucideIcons[name];
      return Icon || Star;
  };

  const schoolName = previewData?.name || "THE ACADEMY";

  // Shared Data Model (Strict Matching Design 1)
  const defaultData = {
    hero: {
      images: ["https://images.unsplash.com/photo-1577896334614-53b708b1a8d0?q=80&w=2070&auto=format&fit=crop"],
      tagline: "EST. 1998 // NAIROBI",
      titlePrefix: "WE FORGE",
      subtitle: "Education is not the learning of facts, but the training of the mind to think. We focus on the raw logic of excellence."
    },
    features: [
      { icon: "Zap", title: "RAW POWER", desc: "Equipping minds with the energy to break boundaries." },
      { icon: "Trophy", title: "STRICT DISCIPLINE", desc: "Performance is a choice. We choose to be elite." },
      { icon: "Users", title: "PURE COMMUNITY", desc: "A network of leaders, not just a group of students." }
    ],
    programs: [
      { title: "LEADERSHIP V1", age: "ELITE FOCUS", desc: "Foundation of authority and critical thought.", img: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=2070&auto=format&fit=crop" },
      { title: "STRATEGY LAB", age: "POST-GRAD", desc: "Advanced systems thinking and global impact.", img: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=2084&auto=format&fit=crop" }
    ],
    stats: [
        { value: "01.", label: "REGION RANK" },
        { value: "99%", label: "IMPACT SCORE" },
        { value: "12k", label: "ALUMNI NODES" }
    ],
    news: [
        { title: "VECTORS OF GROWTH", date: "MAR 20", desc: "Global outreach program expands to three new continents.", img: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=2070&auto=format&fit=crop" }
    ],
    nav: [
        { label: 'MANIFESTO', href: '#features' },
        { label: 'DOCTRINES', href: '#programs' },
        { label: 'ARCHIVES', href: '#gallery' },
        { label: 'COORDINATES', href: '#contact' }
    ],
    team: [
        { name: "COMMANDER S. VANCE", role: "DEAN OF LOGIC", img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=1974&auto=format&fit=crop" },
        { name: "DR. ARIA HUNT", role: "SYSTEMS LEAD", img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1976&auto=format&fit=crop" }
    ],
    gallery: [
        { category: "PHYSICAL", img: "https://images.unsplash.com/photo-1577412647305-991150c7d163?q=80&w=2070&auto=format&fit=crop", title: "THE FOUNDRY" },
        { category: "INTELLECTUAL", img: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071&auto=format&fit=crop", title: "LOGIC CLUSTER" }
    ],
    facilities: [
        { title: "THE VAULT", desc: "High-density physical and digital library.", img: "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?q=80&w=2073&auto=format&fit=crop", icon: "BookOpen" },
        { title: "THE ARENA", desc: "Multi-functional performance and sports center.", img: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=2070&auto=format&fit=crop", icon: "Trophy" }
    ]
  };

  const mockData = {
      ...previewData,
      hero: {
          titlePrefix: previewData?.hero?.titlePrefix || defaultData.hero.titlePrefix,
          tagline: previewData?.hero?.tagline || defaultData.hero.tagline,
          subtitle: previewData?.hero?.subtitle || defaultData.hero.subtitle,
          images: previewData?.hero?.images || defaultData.hero.images,
          backgroundImage: previewData?.hero?.backgroundImage || defaultData.hero.images[0]
      },
      about: {
          title: previewData?.about?.title || "RAW LOGIC. PURE IMPACT.",
          description: previewData?.about?.description || "In a world drowning in data, we provide the clarity of command. THE ACADEMY is not just a school; it is a manufacturing plant for the future's most capable minds.",
          sectionImage: previewData?.about?.sectionImage || "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2070&auto=format&fit=crop"
      },
      features: (previewData?.features || defaultData.features).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || ""
      })),
      programs: (previewData?.programs || defaultData.programs).map((p: any) => ({
        title: p.name || p.title || "",
        age: p.age || "",
        desc: p.description || p.desc || "",
        img: p.image || p.img || ""
      })),
      stats: (previewData?.stats || defaultData.stats).map((s: any) => ({
          value: s.value || "0",
          label: s.label || ""
      })),
      news: (previewData?.news || defaultData.news).map((n: any) => ({
        title: n.title || "",
        date: n.date || "",
        desc: n.description || n.desc || "",
        img: n.image || n.img || ""
      })),
      team: (previewData?.team || defaultData.team).map((t: any) => ({
        name: t.name || "",
        role: t.role || "",
        img: t.img || t.image || "",
        linkedin: t.linkedin || "",
        email: t.email || ""
      })),
      gallery: (previewData?.gallery || defaultData.gallery).map((g: any) => ({
        category: g.category || "General",
        title: g.caption || g.title || "",
        img: g.image || g.img || ""
      })),
      facilities: (previewData?.facilities || defaultData.facilities).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || "",
        img: f.image || f.img || ""
      })),
      downloads: (previewData?.downloads || [
          { title: "LOGIC_CHARTER.v2", size: "1.2 MB", type: "PDF" },
          { title: "ENROLLMENT_MODS.sys", size: "4.5 MB", type: "PKG" }
      ]).map((d: any) => ({
          title: d.title || "",
          size: d.size || "",
          type: d.type || "PDF"
      })),
      contact: {
        address: previewData?.contact?.address || "THE ARCH, 77 SECTOR, NAIROBI",
        phone: previewData?.contact?.phone || "+254 999 000 000",
        email: previewData?.contact?.email || "command@theacademy.edu",
        officeHours: previewData?.contact?.officeHours || "24/7 MONITORING // 0800 - 1800 VISITS",
        facebook: previewData?.contact?.facebook || "",
        instagram: previewData?.contact?.instagram || "",
        youtube: previewData?.contact?.youtube || "",
        linkedin: previewData?.contact?.linkedin || ""
      },
      sectionVisibility: {
          hero: previewData?.sectionVisibility?.hero !== false,
          about: previewData?.sectionVisibility?.about !== false,
          stats: previewData?.sectionVisibility?.stats !== false,
          features: previewData?.sectionVisibility?.features !== false,
          programs: previewData?.sectionVisibility?.programs !== false,
          facilities: previewData?.sectionVisibility?.facilities !== false,
          team: previewData?.sectionVisibility?.team !== false,
          gallery: previewData?.sectionVisibility?.gallery !== false,
          news: previewData?.sectionVisibility?.news !== false,
          downloads: previewData?.sectionVisibility?.downloads !== false,
          cta: previewData?.sectionVisibility?.cta !== false,
          contact: previewData?.sectionVisibility?.contact !== false
      },
      nav: (previewData?.nav || defaultData.nav).map((n: any) => ({
          label: n.label || "",
          href: n.href || "#",
          visible: n.visible !== false
      })),
      labels: {
          downloadsSection: previewData?.labels?.downloadsSection || "The Intelligence",
          downloadsTitle: previewData?.labels?.downloadsTitle || "Data Archives",
          contactFormTitle: previewData?.labels?.contactFormTitle || "Contact Form",
          contactFormSubtitle: previewData?.labels?.contactFormSubtitle || "Fill out the form below."
      }
  };

  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
        const { colors, fonts } = previewData.theme;
        root.style.setProperty('--color-primary', colors.primary || '#E63946');
        root.style.setProperty('--color-secondary', colors.secondary || '#1D3557');
        root.style.setProperty('--color-accent', colors.accent || '#F1FAEE');
        root.style.setProperty('--color-background', colors.background || '#E63946');
        root.style.setProperty('--color-text', colors.text || '#F1FAEE');
        root.style.setProperty('--font-heading', `'${fonts.heading || 'Archivo Black'}', sans-serif`);
        root.style.setProperty('--font-body', `'${fonts.body || 'Inter'}', sans-serif`);
    }
    const fontLink = doc.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Archivo+Black&family=Inter:wght@400;700;900&display=swap";
    fontLink.rel = 'stylesheet';
    doc.head.appendChild(fontLink);
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);
  const addArrayItem = (path: string, newItem: any) => {
    if (onUpdateField) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, [...arr, newItem]);
    }
  };
  const removeArrayItem = (path: string, index: number) => {
    if (onUpdateField && window.confirm('Decommission item?')) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, arr.filter((_, i) => i !== index));
    }
  };

  return (
    <div ref={containerRef} className="bold-typography bg-[var(--color-background)] text-[var(--color-text)] min-h-screen font-[var(--font-body)] selection:bg-[var(--color-secondary)] selection:text-white overflow-x-hidden">
      
      {/* Editorial Header */}
      <header className="p-8 md:p-12 lg:p-20 border-b-2 border-current flex flex-col md:flex-row justify-between items-start md:items-end gap-12">
          <div className="flex flex-col gap-4">
              <div className="flex items-center gap-6">
                   <VisualImageEditable 
                      src={previewData?.logoUrl || ""} 
                      alt="Logo" 
                      containerClassName="w-16 h-16 border-2 border-current p-2"
                      className="w-full h-full object-contain filter invert"
                      onImageChange={(u) => handleUpdate('logoUrl', u)}
                      isEditMode={!!isEditMode}
                      fallback={<div className="font-black text-4xl">/A</div>}
                  />
                  <h1 className="text-7xl md:text-9xl lg:text-[12rem] font-black uppercase tracking-tighter leading-[0.7] font-[var(--font-heading)]">
                      <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
                  </h1>
              </div>
              <div className="flex gap-10 text-[10px] font-black uppercase tracking-[0.5em] mt-8 overflow-x-auto whitespace-nowrap pb-4 md:pb-0">
                  {mockData.nav.filter(n => n.visible).map((n, i) => (
                      <a key={i} href={n.href} className="hover:line-through transition-all">{n.label}</a>
                  ))}
              </div>
          </div>
          <div className="text-right flex flex-col items-end gap-2">
              <div className="text-2xl font-black uppercase tracking-widest bg-current text-[var(--color-background)] px-4 py-1">EST. 1998</div>
              <div className="text-sm font-black opacity-80 uppercase tracking-widest">{mockData.contact.address}</div>
              <button 
                onClick={() => setMenuOpen(!menuOpen)}
                className="mt-8 border-2 border-current px-8 py-3 font-black uppercase tracking-widest hover:bg-current hover:text-[var(--color-background)] transition-all md:hidden"
              >
                MENU
              </button>
          </div>
      </header>

      {/* Hero Section - Maximum Impact */}
      {mockData.sectionVisibility.hero && (
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-[80vh] border-b-2 border-current">
           <div className="p-8 md:p-12 lg:p-20 border-b-2 lg:border-b-0 lg:border-r-2 border-current flex flex-col justify-between">
                <div>
                    <div className="inline-block bg-[var(--color-secondary)] text-white px-6 py-2 text-xs font-black uppercase tracking-[0.4em] mb-12">
                        <VisualTextEditable value={mockData.hero.tagline} onSave={(v) => handleUpdate('hero.tagline', v)} isEditMode={!!isEditMode} />
                    </div>
                    <h2 className="text-6xl md:text-8xl lg:text-9xl font-black uppercase leading-[0.85] tracking-tighter mb-12 font-[var(--font-heading)]">
                         <VisualTextEditable value={mockData.hero.titlePrefix} onSave={(v) => handleUpdate('hero.titlePrefix', v)} isEditMode={!!isEditMode} /><br/>
                         <span className="text-transparent bg-clip-text bg-[var(--color-secondary)] inline-block w-full">THE ELITE.</span>
                    </h2>
                    <p className="text-xl md:text-2xl font-bold uppercase leading-snug max-w-lg mb-12 opacity-80">
                         <VisualTextEditable value={mockData.hero.subtitle} onSave={(v) => handleUpdate('hero.subtitle', v)} isEditMode={!!isEditMode} multiline />
                    </p>
                </div>
                <div className="flex flex-wrap gap-8 items-center">
                    <button className="bg-current text-[var(--color-background)] text-2xl font-black uppercase px-12 py-6 border-4 border-current shadow-[12px_12px_0px_var(--color-secondary)] active:translate-x-2 active:translate-y-2 active:shadow-none transition-all">
                        DEPLOY NOW
                    </button>
                    <div className="hidden md:flex flex-col gap-1">
                        <span className="text-xs font-black uppercase opacity-40">SCROLL_VECTORS</span>
                        <ArrowDown size={32} className="animate-bounce" />
                    </div>
                </div>
           </div>
           <div className="relative overflow-hidden group">
                <VisualImageEditable 
                    src={mockData.hero.backgroundImage} 
                    className="absolute inset-0 w-full h-full object-cover grayscale mix-blend-multiply opacity-70 group-hover:scale-105 group-hover:opacity-100 transition-all duration-1000" 
                    alt="Hero Visual"
                    onImageChange={(u) => handleUpdate('hero.backgroundImage', u)}
                    isEditMode={!!isEditMode}
                />
                <div className="absolute inset-0 p-8 md:p-12 lg:p-20 flex items-center justify-center pointer-events-none">
                    <div className="w-64 h-64 md:w-80 md:h-80 rounded-full border-4 border-current flex items-center justify-center animate-spin-slow">
                        <div className="text-[10px] font-black uppercase tracking-[0.6em] text-center p-8">
                            SCROLL DOWN • SCROLL DOWN • SCROLL DOWN • SCROLL DOWN • SCROLL DOWN
                        </div>
                    </div>
                </div>
           </div>
      </section>
      )}

      {/* About - Manifesto */}
      {mockData.sectionVisibility.about && (
      <section id="about" className="grid grid-cols-1 md:grid-cols-12 border-b-2 border-current">
           <div className="md:col-span-8 p-8 md:p-12 lg:p-20 border-b-2 md:border-b-0 md:border-r-2 border-current">
                <h3 className="text-8xl md:text-[14rem] font-black uppercase tracking-tighter leading-[0.7] mb-12 font-[var(--font-heading)]">01</h3>
                <h4 className="text-4xl md:text-6xl font-black uppercase mb-12 tracking-tighter">
                    <VisualTextEditable value={mockData.about.title} onSave={(v) => handleUpdate('about.title', v)} isEditMode={!!isEditMode} />
                </h4>
                <div className="text-2xl md:text-4xl font-bold uppercase leading-tight max-w-4xl text-justify hyphens-auto border-t-2 pt-12 border-current">
                    <VisualTextEditable value={mockData.about.description} onSave={(v) => handleUpdate('about.description', v)} isEditMode={!!isEditMode} multiline />
                </div>
           </div>
           <div className="md:col-span-4 p-8 md:p-12 lg:p-20 bg-current text-[var(--color-background)] flex flex-col justify-center">
                <div className="space-y-12">
                     {mockData.stats.map((s, i) => (
                         <div key={i} className="group cursor-pointer">
                              <div className="text-5xl md:text-7xl font-black mb-2 flex items-center gap-4">
                                  <VisualTextEditable value={s.value} onSave={(v) => handleUpdate(`stats[${i}].value`, v)} isEditMode={!!isEditMode} />
                                  <ArrowRight size={40} className="group-hover:translate-x-4 transition-transform" />
                              </div>
                              <div className="text-xs font-black uppercase tracking-[0.5em] opacity-40 group-hover:opacity-100">
                                  <VisualTextEditable value={s.label} onSave={(v) => handleUpdate(`stats[${i}].label`, v)} isEditMode={!!isEditMode} />
                              </div>
                         </div>
                     ))}
                </div>
           </div>
      </section>
      )}

      {/* Features - Doctrines */}
      {mockData.sectionVisibility.features && (
      <section id="features" className="p-8 md:p-12 lg:p-20 border-b-2 border-current">
          <div className="flex justify-between items-end mb-20 border-b-2 border-current pb-8">
              <h2 className="text-5xl md:text-8xl font-black uppercase tracking-tighter font-[var(--font-heading)]">DOCTRINES_</h2>
              <div className="text-xs font-black uppercase tracking-widest hidden md:block">CORE PRINCIPLES // V3.0</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-20">
              {mockData.features.map((item, i) => (
                  <div key={i} className="group relative">
                      <div className="w-20 h-20 bg-current text-[var(--color-background)] flex items-center justify-center mb-10 text-4xl group-hover:rotate-12 transition-transform">
                          {React.createElement(item.icon, { size: 40 })}
                      </div>
                      <h3 className="text-4xl font-black uppercase mb-6 tracking-tighter">
                          <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`features[${i}].title`, v)} isEditMode={!!isEditMode} />
                      </h3>
                      <p className="text-xl font-bold uppercase opacity-60 leading-tight">
                          <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`features[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                      </p>
                      {isEditMode && <button onClick={() => removeArrayItem('features', i)} className="text-xs font-black uppercase underline mt-4">Drop</button>}
                  </div>
              ))}
              {isEditMode && (
                  <div onClick={() => addArrayItem('features', { title: "NEW PILLAR", desc: "Brief description.", icon: "Zap" })} className="border-4 border-dashed border-current aspect-square flex flex-col items-center justify-center cursor-pointer hover:bg-current hover:text-[var(--color-background)] transition-all">
                      <Plus size={64}/>
                      <span className="font-black uppercase tracking-widest mt-4">Add Doctrine</span>
                  </div>
              )}
          </div>
      </section>
      )}

      {/* Programs - The Labs */}
      {mockData.sectionVisibility.programs && (
      <section id="programs" className="border-b-2 border-current">
           <div className="grid grid-cols-1 md:grid-cols-2">
                {mockData.programs.map((p, i) => (
                    <div key={i} className={`group relative p-8 md:p-12 lg:p-20 border-b-2 md:border-b-0 ${i === 0 ? 'md:border-r-2' : ''} border-current overflow-hidden cursor-pointer`}>
                        <div className="absolute inset-0 bg-current opacity-0 group-hover:opacity-100 transition-opacity z-0" />
                        <div className="relative z-10 group-hover:text-[var(--color-background)] transition-colors">
                            <span className="text-xs font-black uppercase tracking-[0.5em] mb-4 block">{p.age}</span>
                            <h3 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-8 leading-[0.8] font-[var(--font-heading)]">
                                <VisualTextEditable value={p.title} onSave={(v) => handleUpdate(`programs[${i}].title`, v)} isEditMode={!!isEditMode} />
                            </h3>
                            <p className="text-xl font-bold uppercase max-w-sm mb-12 opacity-80 group-hover:opacity-100">
                                <VisualTextEditable value={p.desc} onSave={(v) => handleUpdate(`programs[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                            </p>
                            <VisualImageEditable 
                                src={p.img} 
                                className="w-full aspect-video object-cover grayscale border-2 border-current shadow-[12px_12px_0px_current] group-hover:shadow-[12px_12px_0px_var(--color-background)] transition-all" 
                                alt={p.title}
                                onImageChange={(u) => handleUpdate(`programs[${i}].img`, u)}
                                isEditMode={!!isEditMode}
                            />
                        </div>
                        {isEditMode && <button onClick={() => removeArrayItem('programs', i)} className="absolute top-4 right-4 z-20 text-xs font-black uppercase underline">Eject</button>}
                    </div>
                ))}
                {isEditMode && (
                    <div onClick={() => addArrayItem('programs', { title: "NEW LAB", age: "ELITE", desc: "Description.", img: "" })} className="p-20 border-4 border-dashed border-current flex flex-col items-center justify-center cursor-pointer hover:bg-current hover:text-[var(--color-background)] transition-all">
                        <Plus size={64}/>
                    </div>
                )}
           </div>
      </section>
      )}

      {/* Team - Authority */}
      {mockData.sectionVisibility.team && (
      <section id="team" className="p-8 md:p-12 lg:p-20 border-b-2 border-current bg-current text-[var(--color-background)]">
          <div className="flex justify-between items-start mb-32 border-b-2 border-[var(--color-background)] pb-12">
              <h2 className="text-6xl md:text-9xl font-black uppercase tracking-tighter leading-none font-[var(--font-heading)] italic">COMMAND.</h2>
              <div className="max-w-xs text-right">
                  <p className="text-sm font-black uppercase tracking-widest opacity-60">The faculty consists of tactical experts in every discipline.</p>
              </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 border-l-2 border-t-2 border-[var(--color-background)]">
              {mockData.team.map((m, i) => (
                  <div key={i} className="p-8 border-r-2 border-b-2 border-[var(--color-background)] group hover:bg-[var(--color-background)] hover:text-current transition-all cursor-pointer">
                      <div className="aspect-[3/4] overflow-hidden mb-8 border-2 border-current grayscale hover:grayscale-0 transition-all duration-700 relative">
                           <VisualImageEditable 
                              src={m.img} 
                              alt={m.name} 
                              className="w-full h-full object-cover" 
                              onImageChange={(u) => handleUpdate(`team[${i}].img`, u)} 
                              isEditMode={!!isEditMode}
                          />
                          <div className="absolute top-4 left-4 bg-current text-[var(--color-background)] px-3 py-1 text-[10px] font-black uppercase tracking-widest">CMD_0{i+1}</div>
                      </div>
                      <h3 className="text-3xl font-black uppercase tracking-tight mb-2 leading-none">
                          <VisualTextEditable value={m.name} onSave={(v) => handleUpdate(`team[${i}].name`, v)} isEditMode={!!isEditMode} />
                      </h3>
                      <p className="text-[10px] font-black uppercase tracking-[0.4em] opacity-40 group-hover:opacity-100">
                          <VisualTextEditable value={m.role} onSave={(v) => handleUpdate(`team[${i}].role`, v)} isEditMode={!!isEditMode} />
                      </p>
                      {isEditMode && <button onClick={() => removeArrayItem('team', i)} className="text-[8px] font-black uppercase underline mt-4">Suspend</button>}
                  </div>
              ))}
              {isEditMode && (
                  <div onClick={() => addArrayItem('team', { name: "NEW CMD", role: "COMMANDER", img: "" })} className="border-r-2 border-b-2 border-[var(--color-background)] flex items-center justify-center p-20 cursor-pointer hover:bg-[var(--color-background)] group">
                      <Plus size={64} className="group-hover:text-current" />
                  </div>
              )}
          </div>
      </section>
      )}

      {/* Gallery - Archives */}
      {mockData.sectionVisibility.gallery && (
      <section id="gallery" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-b-2 border-current">
          {mockData.gallery.map((item, i) => (
              <div key={i} className="relative aspect-square border-b-2 md:border-b-0 md:border-r-2 border-current group overflow-hidden">
                  <VisualImageEditable 
                      src={item.img} alt={item.title}
                      className="absolute inset-0 w-full h-full object-cover grayscale opacity-50 group-hover:opacity-100 group-hover:scale-110 transition-all duration-1000"
                      onImageChange={(u) => handleUpdate(`gallery[${i}].img`, u)}
                      isEditMode={!!isEditMode}
                  />
                  <div className="absolute inset-0 bg-current/20 opacity-0 group-hover:opacity-100 transition-opacity p-8 flex flex-col justify-end">
                      <div className="bg-current text-[var(--color-background)] self-start px-2 py-1 text-[8px] font-black uppercase tracking-widest mb-2 italic">{item.category}</div>
                      <h4 className="text-4xl font-black uppercase text-current leading-[0.7] tracking-tighter">{item.title}</h4>
                  </div>
              </div>
          ))}
          {isEditMode && (
              <div onClick={() => addArrayItem('gallery', { category: "PHYS", title: "NEW ARCHIVE", img: "" })} className="aspect-square flex flex-col items-center justify-center border-r-2 border-current cursor-pointer hover:bg-current hover:text-[var(--color-background)]">
                  <Camera size={48}/>
              </div>
          )}
      </section>
      )}

      {/* News - The Signal */}
      {mockData.sectionVisibility.news && (
      <section id="news" className="p-8 md:p-12 lg:p-20 border-b-2 border-current">
           <div className="flex justify-between items-baseline mb-32 border-b-2 border-current pb-8 gap-12">
               <h2 className="text-6xl md:text-[12rem] font-black uppercase tracking-tighter leading-none font-[var(--font-heading)]">SIGNAL.</h2>
               <div className="hidden md:block h-2 bg-current flex-1 mx-12"></div>
               <div className="text-right">
                   <p className="text-xs font-black uppercase tracking-widest italic opacity-60">Transmission Stream v4.0.1</p>
               </div>
           </div>
           
           <div className="space-y-32">
               {mockData.news.map((item, i) => (
                   <div key={i} className="group relative flex flex-col md:flex-row gap-12 lg:gap-24 items-start cursor-pointer">
                        <div className="w-full md:w-1/3 aspect-video relative overflow-hidden border-4 border-current shadow-[12px_12px_0px_current] group-hover:shadow-[24px_24px_0px_current] transition-all duration-500">
                             <VisualImageEditable 
                                src={item.img} alt={item.title}
                                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000"
                                onImageChange={(u) => handleUpdate(`news[${i}].img`, u)}
                                isEditMode={!!isEditMode}
                            />
                             <div className="absolute top-0 right-0 p-4 bg-current text-[var(--color-background)] font-black text-xs uppercase tracking-widest">{item.date}</div>
                        </div>
                        <div className="w-full md:w-2/3">
                            <h3 className="text-5xl md:text-8xl font-black uppercase tracking-tighter leading-[0.8] mb-8 group-hover:italic transition-all">
                                <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`news[${i}].title`, v)} isEditMode={!!isEditMode} />
                            </h3>
                            <p className="text-2xl font-bold uppercase leading-tight max-w-2xl opacity-60 group-hover:opacity-100">
                                <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`news[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                            </p>
                            <div className="flex items-center gap-6 mt-12 group-hover:translate-x-4 transition-transform">
                                <div className="h-[2px] w-20 bg-current"></div>
                                <span className="font-black text-sm uppercase tracking-widest">ACCESS DATA PACKET</span>
                                <ArrowUpRight size={20}/>
                            </div>
                        </div>
                        {isEditMode && <button onClick={() => removeArrayItem('news', i)} className="absolute top-0 right-0 text-xs font-black uppercase underline">Scrutinize</button>}
                   </div>
               ))}
           </div>
      </section>
      )}

      {/* Downloads - The Ledger */}
      {mockData.sectionVisibility.downloads && (
      <section className="p-8 md:p-12 lg:p-20 border-b-2 border-current bg-current text-[var(--color-background)]">
          <div className="flex flex-col border-b-2 border-[var(--color-background)] pb-12 mb-20">
               <span className="text-xs font-black uppercase tracking-[0.5em] mb-4">
                   <VisualTextEditable 
                       value={mockData.labels.downloadsSection} 
                       onSave={(v) => handleUpdate('labels.downloadsSection', v)} 
                       isEditMode={!!isEditMode} 
                       as="span" 
                   />
               </span>
               <h2 className="text-4xl md:text-8xl font-black uppercase tracking-tighter leading-none font-[var(--font-heading)] italic">
                   <VisualTextEditable 
                       value={mockData.labels.downloadsTitle} 
                       onSave={(v) => handleUpdate('labels.downloadsTitle', v)} 
                       isEditMode={!!isEditMode} 
                       as="span" 
                   />
               </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 border-l-2 border-t-2 border-[var(--color-background)]">
              {mockData.downloads.map((doc, i) => (
                  <div key={i} className="p-10 border-r-2 border-b-2 border-[var(--color-background)] group hover:bg-[var(--color-background)] hover:text-current transition-all relative">
                      <FileText size={48} className="mb-12 group-hover:scale-110 transition-transform" />
                      <h4 className="text-2xl font-black uppercase tracking-tighter leading-none mb-4 truncate">
                        <VisualTextEditable value={doc.title} onSave={(v) => handleUpdate(`downloads[${i}].title`, v)} isEditMode={!!isEditMode} />
                      </h4>
                      <p className="text-[10px] font-black uppercase tracking-[0.4em] opacity-40 mb-12 italic">
                        <VisualTextEditable value={doc.type} onSave={(v) => handleUpdate(`downloads[${i}].type`, v)} isEditMode={!!isEditMode} as="span" /> // 
                        <VisualTextEditable value={doc.size} onSave={(v) => handleUpdate(`downloads[${i}].size`, v)} isEditMode={!!isEditMode} as="span" />
                      </p>
                      <div className="mt-auto pt-8 border-t-2 border-current flex justify-between items-center">
                           <button className="text-xs font-black uppercase tracking-widest hover:line-through">EXTRACT_DATA</button>
                           <Download size={24} />
                      </div>
                      {isEditMode && <button onClick={() => removeArrayItem('downloads', i)} className="absolute top-4 right-4 text-[9px] font-black uppercase underline">Erase</button>}
                  </div>
              ))}
              {isEditMode && (
                  <div onClick={() => addArrayItem('downloads', { title: "NEW_INTEL.pdf", size: "2.1 MB", type: "PDF" })} className="p-20 border-r-2 border-b-2 border-[var(--color-background)] flex items-center justify-center cursor-pointer hover:bg-[var(--color-background)] group">
                      <Plus size={64} className="group-hover:text-current" />
                  </div>
              )}
          </div>
      </section>
      )}

      {/* CTA - Global Command */}
      {mockData.sectionVisibility.cta && (
      <section className="py-64 relative bg-current text-[var(--color-background)] text-center px-6 overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[40rem] font-black opacity-5 pointer-events-none select-none italic tracking-tighter leading-none">JOIN</div>
          <div className="container mx-auto relative z-10 max-w-6xl">
              <h2 className="text-7xl md:text-[12rem] lg:text-[16rem] font-black uppercase tracking-[calc(-0.05em)] leading-[0.7] mb-24 italic drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
                 BECOME THE <br/> AUTHORITY.
              </h2>
              <p className="text-2xl md:text-5xl font-black uppercase leading-[0.85] mb-32 max-w-4xl mx-auto opacity-80">
                The world is waiting for leaders. We are waiting for you. Don't just exist. Lead.
              </p>
              <div className="flex flex-col md:flex-row justify-center gap-12">
                   <button className="bg-[var(--color-background)] text-current h-24 md:h-32 px-16 font-black uppercase tracking-[0.2em] text-2xl border-4 border-[var(--color-background)] shadow-[15px_15px_0px_#000] active:translate-x-2 active:translate-y-2 active:shadow-none transition-all">
                       INITIALIZE_COMMAND()
                   </button>
                   <button className="border-4 border-[var(--color-background)] text-[var(--color-background)] h-24 md:h-32 px-16 font-black uppercase tracking-[0.2em] text-2xl hover:bg-[var(--color-background)] hover:text-current transition-all">
                       QUERY_PROTOCOLS
                   </button>
              </div>
          </div>
      </section>
      )}

      {/* Footer - Final Coordinates */}
      <footer id="contact" className="bg-[var(--color-background)] p-8 md:p-12 lg:p-20 border-t-2 border-current">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 items-start">
               <div className="lg:col-span-6">
                    <h2 className="text-8xl md:text-[14rem] font-black uppercase tracking-tighter leading-none mb-12 font-[var(--font-heading)] italic">
                        <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-20">
                         <div className="space-y-4">
                              <h4 className="text-xs font-black uppercase tracking-[0.5em] opacity-40">COORD // 01</h4>
                              <p className="text-2xl font-black uppercase leading-tight">
                                <VisualTextEditable value={mockData.contact.address} onSave={(v) => handleUpdate('contact.address', v)} isEditMode={!!isEditMode} multiline />
                              </p>
                         </div>
                         <div className="space-y-4">
                              <h4 className="text-xs font-black uppercase tracking-[0.5em] opacity-40">COMM // 02</h4>
                              <p className="text-2xl font-black uppercase leading-tight">
                                <VisualTextEditable value={mockData.contact.phone} onSave={(v) => handleUpdate('contact.phone', v)} isEditMode={!!isEditMode} />
                              </p>
                              <p className="text-sm font-black uppercase underline opacity-80">
                                <VisualTextEditable value={mockData.contact.email} onSave={(v) => handleUpdate('contact.email', v)} isEditMode={!!isEditMode} />
                              </p>
                         </div>
                    </div>
               </div>
               
               <div className="lg:col-span-3">
                    <h4 className="text-xs font-black uppercase tracking-[0.5em] opacity-40 mb-12">DIRECTORY.LOG</h4>
                    <ul className="space-y-6 text-xl font-black uppercase tracking-tighter">
                        {mockData.nav.map((item, i) => (
                            <li key={i}><a href={item.href} className="hover:line-through transition-all flex items-center gap-6"><span>0{i+1}</span> {item.label}</a></li>
                        ))}
                    </ul>
               </div>

               <div className="lg:col-span-3 flex flex-col gap-12 items-end">
                    <h4 className="text-xs font-black uppercase tracking-[0.5em] opacity-40 mb-12">SOCIAL.VECTORS</h4>
                    <div className="grid grid-cols-2 gap-4 w-full">
                         {[Facebook, Instagram, Youtube, Linkedin].map((Icon, i) => (
                             <a key={i} href="#" className="border-2 border-current aspect-square flex items-center justify-center hover:bg-current hover:text-[var(--color-background)] transition-all">
                                 <Icon size={32} />
                             </a>
                         ))}
                    </div>
                    <div className="mt-auto pt-20 text-[10px] font-black uppercase tracking-[0.6em] opacity-20 transform -rotate-90 origin-right">
                        © {new Date().getFullYear()} {schoolName} // GLOBAL_DOMAINS
                    </div>
               </div>
          </div>
      </footer>

      {/* Full Screen Menu */}
      {menuOpen && (
          <div className="fixed inset-0 z-[100] bg-current text-[var(--color-background)] p-8 md:p-12 lg:p-20 flex flex-col animate-in fade-in zoom-in duration-500 overflow-hidden">
               <div className="flex justify-between items-center mb-24 border-b-2 border-[var(--color-background)] pb-12">
                   <h2 className="text-5xl font-black uppercase italic tracking-tighter leading-none">{schoolName}</h2>
                   <button onClick={() => setMenuOpen(false)} className="text-[var(--color-background)]"><X size={64} /></button>
               </div>
               <nav className="flex flex-col gap-8 md:gap-12">
                   {mockData.nav.filter(n => n.visible).map((n, i) => (
                       <a key={i} href={n.href} onClick={() => setMenuOpen(false)} className="text-6xl md:text-9xl lg:text-[12rem] font-black uppercase tracking-tighter leading-[0.7] hover:text-transparent hover:[-webkit-text-stroke:2px_var(--color-background)] transition-all">
                           {n.label}
                       </a>
                   ))}
               </nav>
               <div className="mt-auto flex justify-between items-end">
                    <p className="text-sm font-black uppercase tracking-[0.5em] opacity-40">SELECT_PROTOCOL</p>
                    <div className="text-9xl font-black opacity-10">/2026</div>
               </div>
          </div>
      )}
    </div>
  );
};

export default BoldTypography;
